import "web-ui/tom-select"
import { Controller } from "@hotwired/stimulus";
import { morphdomIgnore } from "web-ui/morphdom/ignore"
import { resetOptions } from "web-ui/tom-select/reset-options"
import * as Log from "web-ui/log"

// Connects to data-controller="async-combo-box"
export default class extends Controller {
  static values = {
    searchUrl: String,
    searchResultValueAttribute: { type: String, default: "id" },
    searchResultLabelAttribute: { type: String, default: "label" },
    searchPlaceholder: { type: String, default: "Search..." },
    clearButtonEnabled: { type: Boolean, default: false }
  }

  connect() {
    this.debug("Connect")

    document.addEventListener("turbo:before-cache", this.beforeTurboCache)

    this.initializeTomSelect()
  }

  initializeTomSelect() {
    const select = this.element.querySelector("select")

    this.debug("Initialize TomSelect", {
      selectId: select.id,
      searchUrl: this.searchUrlValue,
      searchResultValueAttribute: this.searchResultValueAttributeValue,
      searchResultLabelAttribute: this.searchResultLabelAttributeValue,
      searchPlaceholder: this.searchPlaceholderValue
    })

    if (this.tomSelect) {
      this.tomSelect.destroy()
    }

    const hasBlankOption = select.querySelectorAll("option[value='']").length > 0

    const plugins = {
      "no_backspace_delete": {},
      "dropdown_input": { placeholder: this.searchPlaceholderValue },
      "dropdown_placement": {},
      "dropdown_scroll": {},
      "dropdown_open": {},
      "dropdown_close_on_scroll": {},
      "turbo_morph_sync": {},
      "submit_form": {},
      "destroy": {}
    }

    if (this.clearButtonEnabledValue) {
      plugins["clear_button"] = {}
    }

    const body = document.querySelector("body")

    this.tomSelect = new TomSelect(select, {
      plugins: plugins,
      refreshThrottle: 0,
      dropdownParent: body,
      openOnFocus: false,
      allowEmptyOption: hasBlankOption,
      load: this.search,
      loadThrottle: 100,
      sortField: [{ field: "text" }],
      maxOptions: null,

      onFocus: () => {
        morphdomIgnore(this.element, true)
      },

      onBlur: () => {
        if (this.tomSelect.isFocused) {
          return
        }

        morphdomIgnore(this.element, false)
      },

      onDropdownClose: () => {
        resetOptions(select, this.tomSelect)
      },

      onType: (searchTerm) => {
        this.tomSelect.clearCache()

        if (searchTerm === "") {
          if (this.abortController) {
            this.abortController.abort()
            this.tomSelect.loading = 0
          }

          this.ensureDropdownDoesNotClose(() => {
            resetOptions(select, this.tomSelect)
          })
        }
      },

      onItemAdd: (value, $item) => {
        this.clearUnselectedOptions(value)
      },

      onLoad: () => {
        if (!this.tomSelect.loading) {
          this.tomSelect.dropdown_content.classList.remove("loading")
        }
      },

      render: {
        loading: () => '',
        dropdown: () => `<div id="${select.id}-dropdown"></div>`,
        option: (data, escape) => {
          if (this.tomSelect && this.tomSelect.lastQuery === "") {
	    return `<div style="display: none;">${escape(data.text)}</div>`
          }

	  return `<div>${escape(data.text)}</div>`
        },
        item: (data, escape) => {
          return `<div title="${escape(data.text)}">${escape(data.text)}</div>`
        }
      }
    })

    this.tomSelect.hook("instead", "canLoad", () => true)

    this.tomSelect.hook("before", "open", () => {
      this.tomSelect.clearCache()
    })
  }

  searchUrlValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  searchResultValueAttributeValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  searchResultLabelAttributeValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  searchPlaceholderValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  clearButtonEnabledValueChanged() {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  beforeTurboCache = () => {
    this.debug("Before Turbo Cache")

    this.tomSelect.destroy()
  }

  ensureDropdownDoesNotClose = (callback) => {
    const closeAfterSelect = this.tomSelect.settings.closeAfterSelect

    this.tomSelect.settings.closeAfterSelect = false

    callback()

    this.tomSelect.settings.closeAfterSelect = closeAfterSelect
  }

  clearUnselectedOptions = (value) => {
    const unselected = []

    Array.from(this.tomSelect.input.children).forEach((child) => {
      const option = child

      if (option.value !== "" && option.value !== value) {
        unselected.push(option)
      }
    })

    unselected.forEach((option) => {
      this.tomSelect.input.removeChild(option)
    })
  }

  search = async (term, callback) => {
    this.debug("Searching", { url: this.searchUrlValue, searchTerm: term })

    this.tomSelect.dropdown_content.classList.add("loading")

    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = new AbortController()

    const signal = this.abortController.signal

    try {
      const url = `${this.searchUrlValue}?search_term=${encodeURIComponent(term)}`
      const response = await fetch(url, { signal })
      const results = await response.json()

      // As a user types, multiple searches are requested and aborted
      // This insures only the results that match the most current search term
      // are applied - Matt, Scott A, Thu Mar 16 2023
      if (term === this.tomSelect.lastValue) {
        const options = results.map((result) => {
          return {
            value: result[this.searchResultValueAttributeValue],
            text: result[this.searchResultLabelAttributeValue]
          }
        })

        callback(options)
      } else {
        callback([])
      }
    } catch (error) {
      callback([])

      // error code (20) is equivalent to an AbortError
      // See: https://developer.mozilla.org/en-US/docs/Web/API/DOMException#error_names
      // Abort errors will occur anytime a search is in flight and the user
      // types another character - Matt, Scott A, Mon Mar 6 2023
      if (error.code && error.code === 20) {
        return
      }

      Sentry.captureException(error)
    } finally {
      this.abortController = null
    }
  }

  disconnect() {
    this.debug("Disconnect")

    document.removeEventListener("turbo:before-cache", this.beforeTurboCache)

    this.tomSelect.destroy()
  }

  debug(message, data={}) {
    Object.assign(data, {
      id: this.element.id
    })

    Log.debug("Async Combo Box - " + message, data)
  }
}
